import type { Card } from "@braze/web-sdk";

export type BrazeCardType = "classic" | "captioned" | "imageOnly" | "control";

// Content Cards can be tagged (via the dashboard key-value `extras`) for a
// specific placement. Cards without a `class_type` are treated as inbox cards
// for backwards compatibility; cards tagged for another placement (ad,
// carousel, etc.) are kept out of the notification center.
export const NOTIFICATION_CENTER_CLASS_TYPE = "notification_center";

export interface BrazeContentCard {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  url: string;
  linkText: string;
  pinned: boolean;
  viewed: boolean;
  dismissible: boolean;
  isControl: boolean;
  cardType: BrazeCardType;
  extras: Record<string, string>;
  expiresAt: Date | null;
  createdAt: Date | null;
  raw: Card;
}

type RawCardFields = Card & {
  title?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
  linkText?: string;
  dismissible?: boolean;
  dismissed?: boolean;
};

export const mapCard = (card: Card, cardType: BrazeCardType): BrazeContentCard => {
  const raw = card as RawCardFields;

  return {
    id: card.id ?? "",
    title: raw.title ?? "",
    description: raw.description ?? "",
    imageUrl: raw.imageUrl ?? "",
    url: raw.url ?? "",
    linkText: raw.linkText ?? "",
    pinned: Boolean(card.pinned),
    viewed: Boolean(card.viewed),
    dismissible: Boolean(raw.dismissible),
    isControl: Boolean(card.isControl),
    cardType,
    extras: card.extras ?? {},
    expiresAt: card.expiresAt ?? null,
    // Web Content Cards only expose a last-modified timestamp (`updated`);
    // there is no separate "created" field, so we use it as the feed timestamp.
    // Keep it null when absent instead of stamping `new Date()` on every map,
    // which would re-time the card to "now" on every subscription/refresh and
    // keep it pinned to the top of the newest-first feed.
    createdAt: card.updated ?? null,
    raw: card,
  };
};

const isForNotificationCenter = (card: BrazeContentCard): boolean => {
  const classType = card.extras?.class_type;

  return !classType || classType === NOTIFICATION_CENTER_CLASS_TYPE;
};

export const isActiveCard = (card: BrazeContentCard): boolean => {
  if (card.isControl || card.cardType === "control") {
    return false;
  }
  if (!isForNotificationCenter(card)) {
    return false;
  }
  if ((card.raw as RawCardFields).dismissed) {
    return false;
  }
  if (card.expiresAt && card.expiresAt.getTime() <= Date.now()) {
    return false;
  }

  return true;
};

// Lower `card_priority` (from dashboard extras) sorts first; cards without a
// numeric priority sort after prioritized ones. The pinned -> priority ->
// newest ordering lives in `sortFeedEntries` (notification-feed.ts), which is
// the single ordering authority for the merged notification feed.
export const getCardPriority = (card: BrazeContentCard): number => {
  const raw = card.extras?.card_priority;
  const parsed = raw !== undefined ? Number(raw) : NaN;

  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};
